import React, { useEffect, useState, useRef } from 'react';

import Tippy from '@tippyjs/react';
import {
  Edit2,
  Trash,
  X,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  EllipsisVertical,
  SaveIcon,
} from 'lucide-react';

import FloatingMenu from './FloatingMenu';
import Loader from './Loader';
import Pagination from './Pagination';
import { confirmAction } from '../../utils/sweetAlertUtils';
import { defaultExpandedRowRenderer } from '../../utils/tableUtils';

import 'tippy.js/dist/tippy.css';

interface TableColumn {
  label: string | React.ReactNode;
  key: string;
  format?: (value: any, row: any) => string;
  isImage?: boolean;
  imageSize?: string;
  editable?: boolean;
  width?: string;
  render?: (row: any) => React.ReactElement;
  error?: (row: any) => string | null;
  className?: string;
  editTemplate?: (
    value: any,
    onChange: (val: any) => void,
    row: any,
  ) => React.ReactElement;
  priority?: number; // Column priority for dynamic hiding: lower number = higher importance (1 = always visible, 5 = default, 8+ = hide first)
  sortable?: boolean; // Can this column be sorted?
  sortKey?: string; // Backend field name for sorting (e.g., "FirstName")
}

export interface TableAction {
  label: string;
  onClick: (row: any) => void;
  confirmMessage?: string;
  icon?: React.ReactNode;
  className?: string;
}

interface TableProps<T = any> {
  columns: TableColumn[];
  data: any[];
  actions?: {
    onEdit?: (row: T) => void;
    onDelete?: (row: T) => void;
    extra?: TableAction[] | ((row: T) => TableAction[]);
  };
  groupBy?: string[]; // New prop for grouping data
  groupHeaderRender?: (group: string, rows: any[]) => React.ReactElement; // Custom group header render
  allowPaging?: boolean;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  isLoading: boolean;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  noDataMessage?: string;
  enableInlineEditing?: boolean;
  triggerNewRowEdit?: boolean;
  setTriggerNewRowEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  expandableDetails?: boolean; // Enable expandable row details
  renderExpandedRow?: (row: any) => React.ReactElement; // Custom expanded row render
  sortBy?: string; // Current sort field
  sortDescending?: boolean; // Sort direction
  onSort?: (sortKey: string) => void; // Callback when column clicked
  renderRow?: RenderRow; // Supply each data row's own <tr> (e.g. a drag-and-drop sortable row)
}

// Lets a page supply the <tr> for each data row, keeping the table's own cells inside it. Used by
// Courses Played for drag-and-drop ranking, where each row has to be a sortable element with its
// own hook. The returned element must render a <tr> carrying `className` around `children`.
export type RenderRow = (
  row: any,
  rowProps: { className: string; children: React.ReactNode },
) => React.ReactElement;

// The data row's <tr>, or the page's own row when it supplies one. A component rather than an
// inline branch so the long cell markup inside it is written once.
const RowElement: React.FC<{
  row: any;
  renderRow?: RenderRow;
  className: string;
  children: React.ReactNode;
}> = ({ row, renderRow, className, children }) =>
  renderRow ? (
    renderRow(row, { className, children })
  ) : (
    <tr className={className}>{children}</tr>
  );

const Table: React.FC<TableProps> = ({
  columns,
  data,
  actions,
  groupBy,
  groupHeaderRender, // Custom group header render prop
  allowPaging = true,
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  isLoading,
  noDataMessage = 'No records found',
  enableInlineEditing = false,
  triggerNewRowEdit = false,
  setTriggerNewRowEdit,
  expandableDetails = true,
  renderExpandedRow,
  sortBy,
  sortDescending = true,
  onSort,
  renderRow,
}) => {
  // State for dynamically hidden columns
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  const [rowEditIndex, setRowEditIndex] = useState<number | null>(null);
  const [editedRow, setEditedRow] = useState<any>({});
  const [dropdownVisible, setDropdownVisible] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number | string>>(
    new Set(),
  );
  const [shouldShowExpand, setShouldShowExpand] = useState(false);
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<Set<string>>(
    new Set(),
  );
  const tableRef = useRef<HTMLTableElement>(null);
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  // The open row's "more actions" button, which FloatingMenu positions the menu against.
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);

  // Use default expanded row renderer if none provided, but only show hidden columns
  const expandedRowRenderer =
    renderExpandedRow ||
    ((row: any) => {
      const hiddenColumns = columns.filter((col) =>
        hiddenColumnKeys.has(col.key),
      );
      return defaultExpandedRowRenderer(hiddenColumns, row);
    });

  // Dynamic column hiding based on actual available space
  useEffect(() => {
    if (!expandableDetails || !tableRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!tableRef.current || !headerRowRef.current) {
        return;
      }

      const tableWidth = tableRef.current.offsetWidth;
      const cells = Array.from(headerRowRef.current.children) as HTMLElement[];

      // Minimum column width before it becomes unreadable (text can't wrap anymore)
      const MIN_COLUMN_WIDTH = 100; // px - minimum before content becomes unusable

      // Get columns with their priorities (lower number = higher priority to keep visible)
      const columnsWithPriority = columns
        .map((col, index) => {
          let priority = col.priority;

          // Auto-assign priorities if not set
          if (priority === undefined) {
            const key = col.key.toLowerCase();
            if (key === 'title' || key === 'name') {
              priority = 1; // Always visible
            } else if (key === 'createdutc' || key === 'updatedutc') {
              priority = 8; // Hide first
            } else {
              priority = 5; // Medium priority
            }
          }

          return {
            col,
            index,
            priority,
            cell: cells[index + (shouldShowExpand ? 1 : 0)],
          };
        })
        .filter((item) => item.cell); // Filter out undefined cells

      // Calculate minimum required width (all columns at minimum size)
      const actionColumnWidth = 80;
      const minRequiredWidth =
        columnsWithPriority.length * MIN_COLUMN_WIDTH + actionColumnWidth;

      // Only hide columns if table is genuinely too narrow for minimum widths
      const newHiddenKeys = new Set<string>();

      if (tableWidth < minRequiredWidth) {
        // Need to hide some columns - prioritize by priority number
        let availableWidth = tableWidth - actionColumnWidth;

        // Keep highest priority columns (lowest number) that fit at minimum width
        for (const item of [...columnsWithPriority].sort(
          (a, b) => a.priority - b.priority,
        )) {
          if (availableWidth >= MIN_COLUMN_WIDTH || item.priority === 1) {
            availableWidth -= MIN_COLUMN_WIDTH;
          } else {
            newHiddenKeys.add(item.col.key);
          }
        }
      }
      // else: all columns fit, let them wrap naturally - don't hide anything

      // Update hidden columns state
      setHiddenColumnKeys(newHiddenKeys);
      setShouldShowExpand(newHiddenKeys.size > 0);
    });

    resizeObserver.observe(tableRef.current);

    // Initial check
    setTimeout(() => {
      if (tableRef.current) {
        resizeObserver.observe(tableRef.current);
      }
    }, 100);

    return () => resizeObserver.disconnect();
  }, [columns, expandableDetails, data]);

  useEffect(() => {
    if (enableInlineEditing && data.length > 0 && triggerNewRowEdit) {
      const lastRow = data[data.length - 1];
      setRowEditIndex(lastRow.id || data.length - 1);
      setEditedRow(lastRow);
      setTriggerNewRowEdit?.(false);
    }
  }, [data, triggerNewRowEdit]);

  // Group data by specified fields
  const groupedData = groupBy
    ? data.reduce(
        (acc, row) => {
          const groupKey = groupBy.map((key) => row[key]).join(' - ');
          if (!acc[groupKey]) {
            acc[groupKey] = [];
          }
          acc[groupKey].push(row);

          return acc;
        },
        {} as Record<string, any[]>,
      )
    : { '': data };

  const toggleGroup = (group: string): void => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const toggleDropdown = (index: number): void => {
    setDropdownVisible(dropdownVisible === index ? null : index);
  };

  const closeDropdown = (): void => {
    setDropdownVisible(null);
  };

  const toggleExpandedRow = (rowId: number | string): void => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const getResponsiveClass = (col: TableColumn): string =>
    // Check if this column is dynamically hidden
    hiddenColumnKeys.has(col.key) ? 'hidden' : '';
  const startEditing = (idx: number, row: any): void => {
    if (enableInlineEditing) {
      setRowEditIndex(row.id || idx);
      setEditedRow({ ...row });
    } else if (actions && actions.onEdit) {
      actions.onEdit(row);
    }
  };

  const cancelEditing = (): void => {
    setRowEditIndex(null);
    setEditedRow({});
  };

  const saveEdit = (): void => {
    if (actions && actions.onEdit) {
      actions.onEdit(editedRow);
    }
    setRowEditIndex(null);
    setEditedRow({});
  };

  const handleEditChange = (key: string, value: any): void => {
    setEditedRow((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSort = (column: TableColumn): void => {
    if (!column.sortable || !onSort) {
      return;
    }
    const sortKey = column.sortKey || column.key;
    onSort(sortKey);
  };

  const getSortIcon = (column: TableColumn): React.ReactNode => {
    if (!column.sortable) {
      return null;
    }
    const sortKey = column.sortKey || column.key;
    if (sortBy !== sortKey) {
      return null;
    }
    return sortDescending ? (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    );
  };

  return (
    <div className="w-full col-span-full">
      <table
        ref={tableRef}
        className="w-full overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5"
      >
        <thead>
          <tr ref={headerRowRef}>
            {shouldShowExpand && (
              <th className="w-12 border-b border-primary/50 bg-gray-50 px-2 py-3 text-left text-sm font-semibold uppercase tracking-wide text-ink dark:bg-white/10 dark:text-white" />
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                style={{ width: col.width }}
                onClick={() => handleSort(col)}
                // Same header treatment as the expand and Actions columns either side of it:
                // uppercase ink on the gold rule. Without this the data columns read as a
                // different table from the two that flank them.
                className={`border-b border-primary/50 bg-gray-50 px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-ink dark:bg-white/10 dark:text-white ${getResponsiveClass(col)} ${col.className || ''} ${col.sortable ? 'cursor-pointer select-none hover:bg-primary/10 dark:hover:bg-white/20' : ''}`}
              >
                <div className="flex items-center">
                  {col.label}
                  {getSortIcon(col)}
                </div>
              </th>
            ))}
            {actions &&
              (actions.onEdit || actions.onDelete || actions.extra) && (
                <th className="w-24 border-b border-primary/50 bg-gray-50 px-2 py-3 text-left text-sm font-semibold uppercase tracking-wide text-ink dark:bg-white/10 dark:text-white">
                  Actions
                </th>
              )}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={
                  columns.length +
                  (actions ? 1 : 0) +
                  (shouldShowExpand ? 1 : 0)
                }
                className="text-center py-4"
              >
                <Loader />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={
                  columns.length +
                  (actions ? 1 : 0) +
                  (shouldShowExpand ? 1 : 0)
                }
                className="py-8 text-center text-sm text-ink-muted dark:text-white/50"
              >
                {noDataMessage}
              </td>
            </tr>
          ) : (
            Object.entries(groupedData).map(([group, rows]) => {
              const typedRows = rows as any[]; // Explicit type assertion

              return (
                <React.Fragment key={group}>
                  {/* Group Header */}
                  {group && (
                    <tr
                      className="cursor-pointer bg-gray-50 dark:bg-white/10"
                      onClick={() => toggleGroup(group)}
                    >
                      <td
                        colSpan={
                          columns.length +
                          (actions ? 1 : 0) +
                          (shouldShowExpand ? 1 : 0)
                        }
                        className="px-0 py-2 font-semibold text-ink dark:text-white"
                      >
                        <div className="px-4 flex items-center">
                          {/* Left Section: Chevron */}
                          <span className="mr-2">
                            {collapsedGroups[group] ? (
                              <ChevronRight className="w-4 h-4 text-ink-muted dark:text-white/60" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-ink-muted dark:text-white/60" />
                            )}
                          </span>

                          {/* Right Section: Full Flex Container */}
                          <div className="flex flex-1 justify-between items-center">
                            {/* Inject Custom Header */}
                            {groupHeaderRender &&
                              groupHeaderRender(group, typedRows)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Group Rows */}
                  {!collapsedGroups[group] &&
                    typedRows.map((row, idx) => {
                      const rowId = row.id || idx;
                      const isExpanded = expandedRows.has(rowId);

                      return (
                        <React.Fragment key={rowId}>
                          <RowElement
                            row={row}
                            renderRow={renderRow}
                            className="transition-colors hover:bg-primary/5 dark:hover:bg-white/5"
                          >
                            {shouldShowExpand && (
                              <td className="border-b border-gray-100 px-2 py-2 text-xs text-ink dark:border-white/10 dark:text-white/80">
                                <button
                                  onClick={() => toggleExpandedRow(rowId)}
                                  className="text-ink-muted transition-colors hover:text-ink dark:text-white/60 dark:hover:text-white"
                                  aria-label={
                                    isExpanded ? 'Collapse row' : 'Expand row'
                                  }
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-5 h-5" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5" />
                                  )}
                                </button>
                              </td>
                            )}
                            {columns.map((col) => (
                              <td
                                key={col.key}
                                style={{ width: col.width }}
                                className={`px-4 py-2 border-b border-gray-300 text-xs text-gray-700 dark:border-gray-700  dark:text-gray-300 ${getResponsiveClass(col)} ${col.className || ''}`}
                              >
                                {rowEditIndex === (row.id || idx) &&
                                col.editable &&
                                enableInlineEditing ? (
                                  col.editTemplate ? (
                                    col.editTemplate(
                                      editedRow[col.key],
                                      (val) => handleEditChange(col.key, val),
                                      row,
                                    )
                                  ) : (
                                    <input
                                      type="text"
                                      value={editedRow[col.key] || ''}
                                      onChange={(e) =>
                                        handleEditChange(
                                          col.key,
                                          e.target.value,
                                        )
                                      }
                                      className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-white/20 dark:bg-white/10 dark:text-white"
                                    />
                                  )
                                ) : col.render ? (
                                  col.render(row)
                                ) : col.isImage ? (
                                  row[col.key] ? (
                                    <img
                                      src={row[col.key]}
                                      alt=""
                                      className="rounded-full"
                                      style={{
                                        width: col.imageSize || '40px',
                                        height: col.imageSize || '40px',
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="flex items-center justify-center rounded-full bg-primary text-secondary"
                                      style={{
                                        width: col.imageSize || '40px',
                                        height: col.imageSize || '40px',
                                      }}
                                    >
                                      ?
                                    </div>
                                  )
                                ) : col.format ? (
                                  col.format(row[col.key], row)
                                ) : (
                                  row[col.key]
                                )}
                              </td>
                            ))}
                            {actions &&
                              (actions.onEdit ||
                                actions.onDelete ||
                                actions.extra) && (
                                <td className="border-b border-gray-100 px-4 py-2 text-left text-sm dark:border-white/10">
                                  <div className="flex space-x-2 justify-start">
                                    {enableInlineEditing &&
                                    rowEditIndex === (row.id || idx) ? (
                                      <>
                                        <Tippy content="Save">
                                          <button
                                            type="button"
                                            onClick={saveEdit}
                                            className="cursor-pointer rounded-md bg-green-600 p-2 text-white transition-colors hover:bg-green-700"
                                          >
                                            <SaveIcon className="h-4 w-4" />
                                          </button>
                                        </Tippy>
                                        <Tippy content="Cancel">
                                          <button
                                            type="button"
                                            onClick={cancelEditing}
                                            className="cursor-pointer rounded-md bg-ink-muted p-2 text-white transition-colors hover:bg-ink"
                                          >
                                            <X className="h-4 w-4" />
                                          </button>
                                        </Tippy>
                                      </>
                                    ) : (
                                      <>
                                        {actions.onEdit && (
                                          <Tippy content="Edit">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                startEditing(idx, row)
                                              }
                                              className="cursor-pointer rounded-md bg-primary p-2 text-secondary transition-colors hover:bg-opacity-90"
                                            >
                                              <Edit2 className="h-4 w-4" />
                                            </button>
                                          </Tippy>
                                        )}
                                        {actions.onDelete && (
                                          <Tippy content="Delete">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                actions.onDelete?.(row)
                                              }
                                              className="cursor-pointer rounded-md bg-red-600 p-2 text-white transition-colors hover:bg-red-700"
                                            >
                                              <Trash className="h-4 w-4" />
                                            </button>
                                          </Tippy>
                                        )}
                                        {actions.extra &&
                                          (() => {
                                            const extraActions =
                                              typeof actions.extra ===
                                              'function'
                                                ? actions.extra(row)
                                                : actions.extra;

                                            if (
                                              !extraActions ||
                                              extraActions.length === 0
                                            ) {
                                              return null;
                                            }

                                            return (
                                              <div
                                                className="relative"
                                                // Only the open row's button anchors the menu.
                                                ref={
                                                  dropdownVisible === idx
                                                    ? actionsAnchorRef
                                                    : undefined
                                                }
                                              >
                                                <button
                                                  onClick={() =>
                                                    toggleDropdown(idx)
                                                  }
                                                  className="cursor-pointer rounded-md bg-gray-100 p-2 text-ink transition-colors hover:bg-gray-200 dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
                                                >
                                                  <EllipsisVertical className="h-4 w-4" />
                                                </button>
                                                {/* On document.body: the table and its Card both
                                                    clip their overflow, so a menu opened on one of
                                                    the last rows used to be cut off. */}
                                                <FloatingMenu
                                                  anchorRef={actionsAnchorRef}
                                                  open={dropdownVisible === idx}
                                                  onClose={closeDropdown}
                                                  align="end"
                                                >
                                                  <div className="min-w-[200px] overflow-hidden whitespace-nowrap rounded-lg border border-gray-200 bg-white shadow-xl">
                                                    {extraActions.map(
                                                      (action, index) => (
                                                        <button
                                                          key={index}
                                                          onClick={async () => {
                                                            if (
                                                              action.confirmMessage
                                                            ) {
                                                              const proceed =
                                                                await confirmAction(
                                                                  {
                                                                    title:
                                                                      'Are you sure?',
                                                                    text: action.confirmMessage,
                                                                    icon: 'warning',
                                                                    confirmButtonText:
                                                                      'Yes',
                                                                    cancelButtonText:
                                                                      'No',
                                                                  },
                                                                );

                                                              if (proceed) {
                                                                action.onClick(
                                                                  row,
                                                                );
                                                              }
                                                            } else {
                                                              action.onClick(
                                                                row,
                                                              );
                                                            }
                                                            closeDropdown();
                                                          }}
                                                          className={`flex items-center px-4 py-2 hover:bg-gray-100 w-full text-left cursor-pointer text-nowrap ${
                                                            action.className ||
                                                            'text-gray-700'
                                                          }`}
                                                        >
                                                          {action.icon && (
                                                            <span className="mr-2 flex-shrink-0">
                                                              {action.icon}
                                                            </span>
                                                          )}
                                                          <span className="truncate">
                                                            {action.label}
                                                          </span>
                                                        </button>
                                                      ),
                                                    )}
                                                  </div>
                                                </FloatingMenu>
                                              </div>
                                            );
                                          })()}
                                      </>
                                    )}
                                  </div>
                                </td>
                              )}
                          </RowElement>
                          {/* Expanded Details Row */}
                          {shouldShowExpand && isExpanded && (
                            <tr className="bg-gray-50 dark:bg-white/5">
                              <td
                                colSpan={columns.length + (actions ? 1 : 0) + 1}
                                className="border-b border-gray-100 px-4 py-4 dark:border-white/10"
                              >
                                {expandedRowRenderer(row)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
      {!isLoading && data.length > 0 && allowPaging && (
        <Pagination
          currentPage={currentPage ?? 0}
          totalPages={totalPages ?? 0}
          onPageChange={onPageChange}
          pageSize={pageSize ?? 0}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};

export default Table;
